<template>
  <div
    class="dialog-alert"
    :class="{
      'is-bottom-sheet': isBottomSheet,
      'dialog-alert--warning': isWarning,
      'dialog-alert--success': isSuccess
    }"
  >
    <template v-if="isWarning">
      <h2 class="dialog-alert__warning-title">
        {{ header }}
      </h2>

      <span class="dialog-alert__warning-icon" aria-hidden="true">
        <font-awesome-icon icon="exclamation-triangle" />
      </span>

      <p
        v-if="text"
        class="dialog-alert__warning-message"
        data-test="alert-modal-warning-msg"
      >
        {{ text }}
      </p>
    </template>
    <template v-else-if="isSuccess">
      <h2
        v-if="text"
        class="dialog-alert__success-title"
        data-test="alert-modal-success-msg"
      >
        {{ text }}
      </h2>

      <span class="dialog-alert__success-icon" aria-hidden="true">
        <font-awesome-icon icon="check" />
      </span>

      <div v-if="desc" class="dialog-alert__success-description">
        <p v-if="isDescHtml" v-html="desc"></p>
        <p v-else>{{ desc }}</p>
      </div>
    </template>
    <template v-else-if="lineLayout == 'Compact' && !isBottomSheet">
      <div class="text-center mb-3">
        <p class="text-text f-title" v-if="text">{{ text }}</p>
      </div>
      <template v-if="note">
        <div class="mb-2">
          <span class="f-bold f-body-lg">หมายเหตุ *</span> <br />
          <span class="f-body">
            {{ note }}
          </span>
        </div>
      </template>
      <div class="text-center" v-if="showIcon">
        <img
          :src="`${$baseUrl}svg/success.svg`"
          alt=""
          class="dialog-alert__icon mb-3"
          aria-hidden="true"
        />
      </div>
    </template>
    <template v-else>
      <div class="text-center mb-3">
        <p :class="[showIcon ? 'text-text f-title' : 'f-body-lg']" v-if="text">
          {{ text }}
        </p>
      </div>
      <div class="text-center" v-if="showIcon">
        <img
          :src="`${$baseUrl}svg/success.svg`"
          alt=""
          class="dialog-alert__icon"
          aria-hidden="true"
        />
      </div>
      <div class="text-center mt-3" v-if="desc">
        <p
          v-if="isDescHtml"
          :class="[showIcon ? 'text-text f-body-lg' : 'f-body-lg']"
          v-html="desc"
        ></p>
        <p v-else :class="[showIcon ? 'text-text f-body-lg' : 'f-body-lg']">
          {{ desc }}
        </p>
      </div>
      <template v-if="note">
        <div class="mb-2">
          <span class="f-bold f-body-lg">หมายเหตุ *</span> <br />
          <span class="f-body">
            {{ note }}
          </span>
        </div>
      </template>
    </template>

    <hr
      v-if="isWarning || isSuccess"
      class="dialog-alert__divider"
      aria-hidden="true"
    />

    <div
      :class="[
        'modal-qr-v2__actions',
        {
          'dialog-alert__actions--semantic': isWarning || isSuccess
        }
      ]"
    >
      <c-btn
        block
        variant="confirm-primary"
        @click="$emit('confirm')"
        data-test="alert-modal-confirm-btn"
      >
        {{ closeText || $t("confirm") }}
      </c-btn>
      <c-btn
        v-if="isCanCancel"
        block
        variant="outline-primary mt-3"
        @click="$emit('cancel')"
        data-test="alert-modal-cancel-btn"
      >
        {{ cancelText || $t("cancel") }}
      </c-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "DialogAlert",
  emits: ["confirm", "cancel"],
  props: {
    variant: {
      type: String,
      default: ""
    },
    header: {
      type: String,
      default: ""
    },
    text: {
      type: String,
      default: ""
    },
    closeText: {
      type: String,
      default: ""
    },
    cancelText: {
      type: String,
      default: ""
    },
    note: {
      type: String,
      default: ""
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    isCanCancel: {
      type: Boolean,
      default: false
    },
    contentClass: {
      type: String,
      default: ""
    },
    desc: {
      type: String,
      default: ""
    },
    isBottomSheet: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    lineLayout() {
      return this.$lineLayout || "";
    },
    isWarning() {
      return this.variant === "warning";
    },
    isSuccess() {
      return this.variant === "success";
    },
    isDescHtml() {
      return (
        typeof this.desc === "string" && /<\/?[a-z][\s\S]*>/i.test(this.desc)
      );
    }
  }
};
</script>

<style scoped lang="scss">
.dialog-alert {
  // padding: 24px 16px;

  &.is-bottom-sheet {
    // padding: 32px 16px 24px;
  }
}

.text-text {
  margin-bottom: 0;
  font-weight: 600;
}

svg[data-icon="check-circle"] {
  color: var(--primary-color);
}

.dialog-alert__icon {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.dialog-alert--warning {
  text-align: center;
}

.dialog-alert--success {
  text-align: center;
}

.dialog-alert__warning-title {
  margin: 0;
  color: var(--text-color);
  font-family: var(--crm-vv-title-font-family);
  font-size: var(--font-title-sm);
  font-weight: 600;
  line-height: 1.4;
}

.dialog-alert__warning-icon {
  display: inline-flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  border-radius: 50%;
  background: var(--crm-vv-warning-background);
  color: var(--crm-vv-warning-color);
  font-size: var(--font-heading);
}

.dialog-alert--warning .dialog-alert__warning-message {
  margin: 20px 0 0;
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 400;
  line-height: 1.6;
  white-space: pre-line;
}

.dialog-alert__success-title {
  margin: 0;
  color: var(--text-color);
  font-family: var(--crm-vv-title-font-family);
  font-size: var(--font-title-sm);
  font-weight: 600;
  line-height: 1.4;
}

.dialog-alert__success-icon {
  display: inline-flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  border-radius: 50%;
  background: var(--primary-color-low);
  color: var(--crm-success-color);
}

.dialog-alert__success-icon :deep(svg) {
  box-sizing: content-box;
  width: 12px;
  height: 12px;
  padding: 3px;
  border: 2px solid currentColor;
  border-radius: 50%;
}

.dialog-alert--success .dialog-alert__success-description {
  margin-top: 20px;
  color: var(--text-color);
  font-size: var(--font-body-lg);
  line-height: 1.5;
}

.dialog-alert--success .dialog-alert__success-description p {
  margin: 0;
  color: inherit;
  font-weight: 400;
}

.dialog-alert__divider {
  margin: 20px -20px 16px;
  border: 0;
  border-top: 1px solid var(--crm-border-color);
  opacity: 1;
}

.dialog-alert__actions--semantic {
  margin-top: 0;
}
</style>
